import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Check, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PayDeposit() {
  const { bookingId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [booking, setBooking] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [paying, setPaying] = useState(false);
  const [pollState, setPollState] = useState(null); // checking | paid | failed | timeout

  const fetchBooking = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/${bookingId}/deposit`);
      setBooking(data);
    } catch {
      setLoadError('We could not find this booking. Please check your link.');
    }
  }, [bookingId]);

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 6;
    if (attempts >= maxAttempts) {
      setPollState('timeout');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/checkout/status/${sid}`);
      if (data.payment_status === 'paid') {
        setPollState('paid');
        fetchBooking();
        return;
      }
      if (data.status === 'expired') {
        setPollState('failed');
        return;
      }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch {
      setPollState('failed');
    }
  }, [fetchBooking]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  useEffect(() => {
    if (sessionId) {
      setPollState('checking');
      pollStatus(sessionId);
    }
  }, [sessionId, pollStatus]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setPaying(false);
      setLoadError(err.response?.data?.detail || 'Could not start payment. Please try again.');
    }
  };

  const Shell = ({ children }) => (
    <main className="min-h-screen flex items-center justify-center px-6 bg-[#020408]" data-testid="pay-deposit-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass rounded-2xl p-8 md:p-10 w-full max-w-md text-center"
      >
        {children}
      </motion.div>
    </main>
  );

  if (loadError && !booking) {
    return (
      <Shell>
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Booking Not Found</h1>
        <p className="text-gray-400" data-testid="pay-deposit-error">{loadError}</p>
      </Shell>
    );
  }

  if (!booking) {
    return (
      <Shell>
        <Loader2 className="w-10 h-10 text-[#00F0FF] mx-auto animate-spin" />
        <p className="text-gray-400 mt-4">Loading your booking...</p>
      </Shell>
    );
  }

  // Payment status views
  if (pollState === 'checking') {
    return (
      <Shell>
        <Loader2 className="w-10 h-10 text-[#00F0FF] mx-auto animate-spin" />
        <h1 className="font-heading text-2xl font-bold mt-4 mb-2">Confirming Payment...</h1>
        <p className="text-gray-400" data-testid="pay-deposit-checking">Please wait while we verify your deposit.</p>
      </Shell>
    );
  }

  if (pollState === 'paid' || booking.deposit_paid) {
    return (
      <Shell>
        <div className="w-20 h-20 rounded-full bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-6 cyan-glow">
          <Check className="w-10 h-10 text-[#00F0FF]" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-3" data-testid="pay-deposit-success">Deposit Paid!</h1>
        <p className="text-gray-400">
          Thank you, {booking.customer_name}. Your deposit for the {booking.service_name} service is confirmed.
          We'll see you on {booking.preferred_date} at {booking.preferred_time}.
        </p>
      </Shell>
    );
  }

  if (pollState === 'timeout' || pollState === 'failed') {
    return (
      <Shell>
        <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Payment Not Confirmed</h1>
        <p className="text-gray-400 mb-6">
          We couldn't confirm your payment yet. If you completed checkout, it may take a moment. You can retry below.
        </p>
        <Button onClick={() => { setSearchParams({}); setPollState(null); }} className="btn-primary rounded-none px-8 py-5 font-heading">
          BACK TO DEPOSIT
        </Button>
      </Shell>
    );
  }

  // Estimate not yet set by admin
  if (booking.deposit_amount == null) {
    return (
      <Shell>
        <Wallet className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Deposit Pending</h1>
        <p className="text-gray-400">
          Hi {booking.customer_name}, we're still preparing the estimate for your {booking.service_name} request.
          You'll be able to pay your deposit here once it's ready.
        </p>
      </Shell>
    );
  }

  // Ready to pay
  return (
    <Shell>
      <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow mx-auto mb-6">
        <Wallet className="w-7 h-7 text-[#00F0FF]" />
      </div>
      <h1 className="font-heading text-2xl font-bold mb-2">Pay Your Deposit</h1>
      <p className="text-gray-400 mb-6">
        {booking.customer_name}, secure your {booking.service_name} appointment on {booking.preferred_date} at {booking.preferred_time}.
      </p>

      <div className="bg-[#0A0F14] border border-[#1E293B] rounded-xl p-6 mb-6 text-left space-y-2">
        <div className="flex justify-between text-gray-400 text-sm">
          <span>Service</span><span className="text-white">{booking.service_name}</span>
        </div>
        <div className="flex justify-between text-gray-400 text-sm">
          <span>Estimate</span><span className="text-white">${booking.estimate_amount?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between border-t border-[#1E293B] pt-2 mt-2">
          <span className="text-gray-300 font-medium">Deposit Due</span>
          <span className="text-[#00F0FF] text-xl font-bold" data-testid="pay-deposit-amount">${booking.deposit_amount.toFixed(2)}</span>
        </div>
      </div>

      {loadError && <p className="text-red-400 text-sm mb-4" data-testid="pay-deposit-pay-error">{loadError}</p>}

      <Button onClick={handlePay} disabled={paying} data-testid="pay-deposit-button" className="w-full btn-primary rounded-none py-6 font-heading tracking-wide">
        {paying ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> REDIRECTING...</span> : `PAY $${booking.deposit_amount.toFixed(2)} DEPOSIT`}
      </Button>
      <p className="text-gray-500 text-xs mt-4 flex items-center gap-1 justify-center">
        <ShieldCheck className="w-3.5 h-3.5" /> Secure payment powered by Stripe
      </p>
    </Shell>
  );
}
